import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import moment from 'moment'
import classnames from 'clsx'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/depositsUtils'
import { hasIncludedGuestsExceeded } from 'lib/cartReservationUtils'
import { pluralizeToString } from 'lib/pluralize'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import GeoContext from 'contexts/geoContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import TextButton from 'components/Luxkit/Button/TextButton'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import Pill from 'components/Luxkit/Pill'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const PackagePrices = styled(Caption)`
  display: flex;
  flex: 1;

  &.show_discount {
    margin-top: ${rem(8)};
  }
`

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const DurationCaption = styled(Caption)`
  margin-top: ${rem(8)};
`

const TagWrapper = styled.div`
  display: flex;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    margin-top: ${rem(8)};
  }
`

const StyledSelectButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  isLoggedIn: boolean;
  checkIn: moment.Moment;
  fetching?: boolean;
  currency: string;
  addAllFlights?: boolean;
  buyNowBookLater?: boolean;
  isSelected: boolean;
  room: App.RoomOccupants;
  onSelect: (packageOption: App.Package) => void;
  defaultDepositAmountPercentage: number;
}

function MultipleRoomsBookingModalPriceDetails(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    fetching,
    currency,
    buyNowBookLater,
    isSelected,
    room,
    onSelect,
    defaultDepositAmountPercentage,
  } = props

  const {
    package: pkg,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { showTaxExclusivePrices } = useContext(GeoContext)
  const showIncludedGuestsExceeded = useMemo(() => !!room && hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const {
    hotelPrice,
    flightPrice,
    taxesAndFees,
    propertyFees,
  } = packageView

  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkIn, hotelPrice) && !buyNowBookLater

  const onSelectRoom = useCallback(() => onSelect(pkg), [onSelect, pkg])

  return (
    <PriceDetails>
      <TagWrapper>
        {isDepositEnabled &&
          <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>
        }
      </TagWrapper>
      <PackagePrices variant="medium" className={classnames({ show_discount: shouldShowDiscount || isDepositEnabled })}>
        <PackagePrice data-testid="pricing">
          <DurationCaption variant="medium">{pluralizeToString('night', pkg.duration)}</DurationCaption>
          <Heading variant="heading6" colour={exclusivePriceColor}>
            <FormatCurrency value={totalPrice} format="roundedDollar" />
          </Heading>
          {shouldShowValue &&
            <ValueWrapper variant="medium">
              <span data-testid="value-box">
                Valued up to <FormatCurrency value={offer.bundledWithFlightsOnly ? hotelValue + flightPrice : hotelValue} />
              </span>
              {shouldShowDiscount && <Pill kind="primary" variant="success">- {highestDiscountPercent}%</Pill>}
            </ValueWrapper>
            }
          <TaxesAndFees
            taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
            taxAmount={taxesAndFees}
          />
          {showIncludedGuestsExceeded && (
            <AdditionalGuestsPopup
              complex={false}
              description="+ Extra guest surcharge"
              modalContent={pkg.roomPolicyDescription}
            />
          )}

        </PackagePrice>
        <LoadingIndicator visible={fetching} floating opaque />
      </PackagePrices>

      <StyledSelectButton kind={isSelected ? 'primary' : 'secondary'} fit="flex" disabled={isSelected} onClick={onSelectRoom}>
        {isSelected ? 'Selected' : 'Select'}
      </StyledSelectButton>
    </PriceDetails>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(MultipleRoomsBookingModalPriceDetails)
